import React, { useState, useEffect } from "react";
import { database, ref, push, onValue, remove, set, update } from "./firebase";
import "./BillingSystem.css";

const products = [
  "Premium 2 Litre Bottle",
  "Premium 1 Litre Bottle",
  "Premium 500ML Bottle",
  "Premium 250ML Bottle",
  "Green 1 Litre Bottle",
  "Green 500ML Bottle",
  "250ML Pouch",
  "20 Litre Jar",
  "Cool Jar",
  "Packed Ice Cube (500 gm)"
];

// Define carton sizes
const cartonSizes = {
  "Premium 2 Litre Bottle": 9,
  "Premium 1 Litre Bottle": 12,
  "Premium 500ML Bottle": 24,
  "Premium 250ML Bottle": 24,
  "Green 500ML Bottle": 24,
};

export default function BillingSystem({ role, phone }) {
  const [quantities, setQuantities] = useState(Array(products.length).fill(0));
  const [schemes, setSchemes] = useState(Array(products.length).fill(0));
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [totalAmount, setTotalAmount] = useState("");
  const [tokenNumber, setTokenNumber] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [userName, setUserName] = useState("");
  const [stock, setStock] = useState(Array(products.length).fill(0));
  const [pendingScheme, setPendingScheme] = useState(Array(products.length).fill(0));
  const [showProfile, setShowProfile] = useState(false);
  const [editName, setEditName] = useState("");
  const [newUserPhone, setNewUserPhone] = useState("");
  const [newUserRole, setNewUserRole] = useState("staff");
  const [newUserName, setNewUserName] = useState("");
  const [notification, setNotification] = useState("");

  const showNotification = (message) => {
    setNotification(message);
    setTimeout(() => setNotification(""), 3000);
  };

  const getTodayDate = () => {
    const now = new Date();
    return now.toISOString().split("T")[0];
  };

  useEffect(() => {
    const userRef = ref(database, `users/${phone}`);
    onValue(userRef, (snapshot) => {
      const data = snapshot.val();
      const name = data?.name || phone;
      setUserName(name);
      setEditName(name);
    }, { onlyOnce: true });

    const today = getTodayDate();
    onValue(ref(database, `token/${today}`), snap => setTokenNumber(snap.val() || 0));

    const updateStock = () => {
      onValue(ref(database, "production"), (prodSnap) => {
        const produced = Array(products.length).fill(0);
        Object.values(prodSnap.val() || {}).forEach(entry => {
          entry.items.forEach(({ product, quantity }) => {
            const i = products.indexOf(product);
            if (i >= 0) produced[i] += quantity;
          });
        });

        onValue(ref(database, "invoices"), (invSnap) => {
          const sold = Array(products.length).fill(0);
          const scheme = Array(products.length).fill(0);
          const data = Object.entries(invSnap.val() || {}).map(([id, val]) => ({ id, ...val }));
          data.forEach(entry => {
            entry.items.forEach(({ product, quantity, scheme: sc = 0 }) => {
              const i = products.indexOf(product);
              if (i >= 0) {
                sold[i] += quantity;
                scheme[i] += sc;
              }
            });
          });

          const stockUpdated = produced.map((p, i) => p - sold[i]);
          setStock(stockUpdated);
          setPendingScheme(scheme);
          setInvoices(data.reverse());
        });
      });
    };

    updateStock();
  }, [phone]);

  const handleQuantityChange = (i, val) => {
    const updated = [...quantities];
    updated[i] = parseInt(val) || 0;
    setQuantities(updated);
  };

  const handleSchemeChange = (i, val) => {
    const updated = [...schemes];
    const value = parseFloat(val);
    updated[i] = isNaN(value) ? 0 : value;
    setSchemes(updated);
  };

  const handleGenerateInvoice = async () => {
    if (!customerPhone.match(/^\d{10}$/)) return showNotification("Invalid phone number");
    if (!quantities.some(q => q > 0)) return showNotification("Enter at least one quantity");

    const today = getTodayDate();
    const tokenRef = ref(database, `token/${today}`);

    let currentToken = 0;
    await new Promise(res => onValue(tokenRef, snap => {
      currentToken = snap.val() || 0;
      res();
    }, { onlyOnce: true }));

    const newToken = currentToken + 1;
    await set(tokenRef, newToken);

    const items = products.map((p, i) => ({
      product: p,
      quantity: quantities[i],
      scheme: schemes[i]
    })).filter(i => i.quantity > 0 || i.scheme > 0);

    const invoice = {
      tokenNumber: newToken,
      customerName,
      customerPhone,
      createdBy: userName,
      paymentMethod,
      totalAmount: totalAmount || "",
      date: new Date().toLocaleString(),
      items
    };

    await push(ref(database, "invoices"), invoice);
    setQuantities(Array(products.length).fill(0));
    setSchemes(Array(products.length).fill(0));
    setCustomerName("");
    setCustomerPhone("");
    setTotalAmount("");
    setPaymentMethod("Cash");
    setTokenNumber(newToken);
    showNotification("Invoice created successfully!");
  };
  const printInvoice = (invoice) => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html>
      <head>
        <title>Jauhari Bill</title>
        <style>
          @media print {
            @page { size: 4in 6in; margin: 0.3in; }
          }
          body { font-family: Arial; font-size: 12px; padding: 10px; }
          h2 { text-align: center; margin-bottom: 10px; font-size: 16px; }
          .line { margin: 2px 0; }
        </style>
      </head>
      <body>
        <h2>Jauhari Bill</h2>
        <div class="line"><strong>Token:</strong> ${invoice.tokenNumber}</div>
        <div class="line"><strong>Name:</strong> ${invoice.customerName}</div>
        <div class="line"><strong>Phone:</strong> ${invoice.customerPhone}</div>
        <div class="line"><strong>Date:</strong> ${invoice.date}</div>
        <div class="line"><strong>Created By:</strong> ${invoice.createdBy}</div>
        <div class="line"><strong>Payment:</strong> ${invoice.paymentMethod}</div>
        ${invoice.totalAmount ? `<div class="line"><strong>Total:</strong> ₹${invoice.totalAmount}</div>` : ""}
        <br/>
        ${invoice.items.map(item => `  
          <div class="line">• ${item.quantity} x ${item.product}${item.scheme > 0 ? ` (Scheme: ${item.scheme})` : ""}</div>
        `).join("")}
        <script>window.print();</script>
      </body>
      </html>
    `);
    win.document.close();
  };

  const sendWhatsApp = (invoice) => {
    const msg = `Jauhari Bill\nToken: ${invoice.tokenNumber}\nCustomer: ${invoice.customerName}\nCreated By: ${invoice.createdBy}\nDate: ${invoice.date}\nPayment: ${invoice.paymentMethod}${invoice.totalAmount ? `\nTotal: ₹${invoice.totalAmount}` : ""}\n` +
      invoice.items.map(i => `${i.quantity} x ${i.product}${i.scheme > 0 ? ` (Scheme: ${i.scheme})` : ""}`).join("\n");
    window.open(`https://wa.me/91${invoice.customerPhone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const downloadCSV = () => {
    const headers = ["Token", "Customer", "Phone", "Date", "Created By", "Payment", "Total", ...products.flatMap(p => [p, `${p} (Scheme)`])];
    const rows = invoices.map(inv => {
      const map = Object.fromEntries(inv.items.map(i => [i.product, [i.quantity, i.scheme || 0]]));
      return [
        inv.tokenNumber, inv.customerName, inv.customerPhone, inv.date, inv.createdBy, inv.paymentMethod, inv.totalAmount || "",
        ...products.flatMap(p => map[p] || [0, 0])
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "tokens.csv";
    link.click();
  };

  const filteredInvoices = invoices.filter(inv => {
    if (role === "staff") {
      return inv.tokenNumber.toString().includes(searchTerm);
    } else {
      return (inv.customerName?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
             inv.tokenNumber.toString().includes(searchTerm);
    }
  });

  const handleAddUser = async () => {
    if (role !== "owner") return showNotification("Only owner can add users");
    if (!newUserPhone.match(/^\d{10}$/) || !newUserName.trim()) return showNotification("Fill all user details");
    await set(ref(database, `users/${newUserPhone}`), { name: newUserName, role: newUserRole });
    setNewUserName("");
    setNewUserPhone("");
    setNewUserRole("staff");
    showNotification("User added successfully");
  };

  const resetTokenNumber = async () => {
    const today = getTodayDate();
    await set(ref(database, `token/${today}`), 0);
    setTokenNumber(0);
    showNotification("Token reset to 0");
  };

  const editInvoice = async (invoice) => {
    const newName = prompt("Edit Customer Name", invoice.customerName);
    const updatedItems = products.map((p, i) => {
      const existing = invoice.items.find(it => it.product === p) || { quantity: 0, scheme: 0 };
      const quantity = parseInt(prompt(`Quantity for ${p}`, existing.quantity)) || 0;
      const scheme = parseFloat(prompt(`Scheme for ${p}`, existing.scheme)) || 0;
      return quantity || scheme ? { product: p, quantity, scheme } : null;
    }).filter(Boolean);

    await update(ref(database, `invoices/${invoice.id}`), {
      customerName: newName,
      items: updatedItems
    });
    showNotification("Invoice updated");
  };

  const deleteInvoice = async (id) => {
    if (window.confirm("Delete this invoice?")) {
      await remove(ref(database, `invoices/${id}`));
      showNotification("Invoice deleted");
    }
  };

  const updateUserProfile = async () => {
    if (!editName.trim()) return showNotification("Enter a valid name");
    await update(ref(database, `users/${phone}`), { name: editName });
    setUserName(editName);
    showNotification("Profile updated");
  };
  return (
    <div className="container">
      <h2>Jauhari Billing System</h2>
      {notification && <div className="notification-banner">{notification}</div>}
      <p>Logged in as <strong>{userName}</strong> ({role}) <button onClick={() => setShowProfile(true)}>Edit Profile</button></p>

      {role === "owner" && (
        <div className="section">
          <h4>Add New User</h4>
          <input placeholder="Phone" value={newUserPhone} onChange={e => setNewUserPhone(e.target.value)} />
          <input placeholder="Name" value={newUserName} onChange={e => setNewUserName(e.target.value)} />
          <select value={newUserRole} onChange={e => setNewUserRole(e.target.value)}>
            <option value="staff">Staff</option>
            <option value="manager">Manager</option>
            <option value="production">Production Manager</option>
            <option value="stock manager">Stock Manager</option>
          </select>
          <button onClick={handleAddUser}>Add User</button>
          <button onClick={resetTokenNumber} style={{ marginLeft: 5 }}>Reset Token</button>
        </div>
      )}

      {(role === "owner" || role === "manager") && (
        <div className="section">
          <h4>Stock Live View</h4>
          <table className="stock-table">
            <thead>
              <tr>
                <th>Product Name</th>
                <th>Scheme Bottles</th>
                <th>Total Stock</th>
                <th>Total Bottles</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p, i) => {
                const cartonSize = cartonSizes[p] || 12;
                const schemeLeft = pendingScheme[i] % cartonSize;
                const totalBottles = schemeLeft === 0 ? 0 : cartonSize - schemeLeft;

                const schemeCartons = Math.floor(pendingScheme[i] / cartonSize);
                const schemeDecimal = schemeLeft / cartonSize;
                let totalCartons = stock[i] - schemeCartons + schemeDecimal;
                if (schemeLeft >= 1) totalCartons -= 1;

                const finalStock = Math.floor(totalCartons); // SHOW WHOLE CARTONS ONLY

                return (
                  <tr key={i}>
                    <td>{p}</td>
                    <td>{schemeLeft.toFixed(2)} bottles</td>
                    <td>{finalStock} cartons</td>
                    <td>{totalBottles.toFixed(2)} bottles</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {role !== "staff" && (
        <div className="section">
          <h4>Create Invoice</h4>
          <div className="product-row-space">
            <span className="product-label"><strong>Product</strong></span>
            <span className="product-input-heading"><strong>Qty</strong></span>
            <span className="product-input-heading"><strong>Scheme</strong></span>
          </div>
          {products.map((p, i) => (
            <div key={i} className="product-row-space">
              <span className="product-label">{p}</span>
              <input type="number" className="product-input-box" value={quantities[i]} onChange={e => handleQuantityChange(i, e.target.value)} />
              <input type="number" className="product-input-box" value={schemes[i]} onChange={e => handleSchemeChange(i, e.target.value)} />
            </div>
          ))}
          <input placeholder="Customer Name" value={customerName} onChange={e => setCustomerName(e.target.value)} />
          <input placeholder="Phone Number" value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} />
          <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
            <option value="Cash">Cash</option>
            <option value="Google Pay">Google Pay</option>
            <option value="Free">Free</option>
            <option value="Pay Later">Pay Later</option>
          </select>
          <input placeholder="Total Amount (optional)" value={totalAmount} onChange={e => setTotalAmount(e.target.value)} />
          <button onClick={handleGenerateInvoice}>Create Invoice</button>
        </div>
      )}

      <div className="section">
        <h4>Token History</h4>
        <input placeholder="Search by name or token" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        {filteredInvoices.length === 0 ? (
          <p>No tokens found.</p>
        ) : (
          filteredInvoices.map(inv => (
            <div key={inv.id} className="token-card">
              <strong>Token #{inv.tokenNumber}</strong> - {inv.customerName} ({inv.customerPhone})<br />
              <small>{inv.date}</small><br />
              <small><strong>Bill Created By:</strong> {inv.createdBy}</small><br />
              <small><strong>Payment:</strong> {inv.paymentMethod}</small>
              {inv.totalAmount && <><br /><small><strong>Total:</strong> ₹{inv.totalAmount}</small></>}
              <ul>{inv.items.map((item, idx) => (
                <li key={idx}>{item.quantity} x {item.product}{item.scheme > 0 ? ` (Scheme: ${item.scheme})` : ""}</li>
              ))}</ul>
              {(role === "owner" || role === "manager") && (
                <>
                  <button onClick={() => printInvoice(inv)}>Print</button>
                  <button onClick={() => sendWhatsApp(inv)}>WhatsApp</button>
                  <button onClick={() => editInvoice(inv)}>Edit</button>
                  <button onClick={() => deleteInvoice(inv.id)}>Delete</button>
                </>
              )}
            </div>
          ))
        )}
        <button onClick={downloadCSV}>Download CSV</button>
      </div>

      {showProfile && (
        <div className="container">
          <h3>My Profile</h3>
          <p><strong>Phone:</strong> {phone}</p>
          <p><strong>Role:</strong> {role}</p>
          <label>Edit Name:</label>
          <input value={editName} onChange={e => setEditName(e.target.value)} />
          <button onClick={updateUserProfile}>Update</button>
          <button onClick={() => setShowProfile(false)} style={{ marginLeft: 10 }}>Back</button>
        </div>
      )}
    </div>
  );
}
